import type { MouseEvent } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import { tokens, IconButton } from '@hotjar/hotsauce';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { PLAYBACKS } from '@hotjar/webapp/constants/routes';
import { trackClick } from '@hotjar/webapp/features/Playbacks/utils/tracking';

type Props = {
  isDisabled: boolean;
  isLoggedIn: boolean;
  recording?: Recording;
};

const StyledLogoIcon = styled(IconButton)`
  svg {
    fill: ${tokens.BRAND_COLORS.ember_700};
  }
`;

const track = (recording: Recording | undefined, element: string) => {
  if (recording) {
    trackClick(recording, {
      element_name: element,
      state_event_was_triggered_on: 'playback.player_header',
      ui_position: 'clicked_icon',
      url_event_was_triggered_on: window.location.href,
    });
  }
};

const preventDefault = (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault();
const hasReferrer = () =>
  Boolean(
    document.referrer &&
      document.referrer.match(/https?:\/\/.*hotjar.com(\/.*|$)/) &&
      !document.referrer.includes('/r?') &&
      !document.referrer.includes('/login/mfa/'),
  );

const getBackUrl = (siteId: number | undefined) => {
  if (hasReferrer()) {
    return document.referrer;
  }

  return getRoute(PLAYBACKS.LIST.path, { siteId: siteId || '' });
};

export const BackButton = ({ isDisabled, isLoggedIn, recording }: Props): JSX.Element => {
  const { t } = useTranslation();
  const siteId = recording?.site_id;

  if (!isLoggedIn) {
    return (
      <StyledLogoIcon
        as="a"
        data-testid="button-sign-in"
        href="/"
        size="s"
        onClick={
          isDisabled
            ? preventDefault
            : () => {
                // Workaround for RECS-1487
                track(recording, 'Hotjar_logo-go_to_hotjar_app');
                window.location.href = '/';
              }
        }
        icon="hotjar"
        kind="ghost"
        tooltipText={t('common:recordings.player.go-to-hotjar')}
        description={t('common:recordings.player.go-to-hotjar')}
      />
    );
  }

  return (
    <IconButton
      as="a"
      size="s"
      data-testid="button-back"
      href={isDisabled ? '' : getBackUrl(siteId)}
      onClick={
        isDisabled
          ? preventDefault
          : () => {
              // Workaround for RECS-1487
              track(recording, 'back_icon-all_recordings');
              window.location.href = getBackUrl(siteId);
            }
      }
      icon="arrow_left_alone"
      kind="ghost"
      tooltipText={
        hasReferrer()
          ? t('common:recordings.player.go-back')
          : t('common:recordings.player.all-recordings')
      }
      description={
        hasReferrer()
          ? t('common:recordings.player.go-back')
          : t('common:recordings.player.all-recordings')
      }
    />
  );
};
